import { useState } from 'react';
import { Plus, Pencil, Trash2, ArrowUpRight, ArrowDownRight, X } from 'lucide-react';
import type {
  ProjectoInvoice,
  ProjectoInvoiceInsert,
  ProjectoInvoiceType,
  ProjectoInvoiceStatus,
} from '../../types/projecto';
import { PROJECTO_INVOICE_STATUS_CONFIG } from '../../types/projecto';
import { projectoInvoiceService } from '../../services/projectoService';
import { Button } from '../ui/Button';

interface Props {
  projectoId: string;
  invoices: ProjectoInvoice[];
  onInvoicesChange: (invoices: ProjectoInvoice[]) => void;
}

const STATUSES: ProjectoInvoiceStatus[] = ['Draft', 'Issued', 'Partially Paid', 'Paid', 'Overdue', 'Cancelled'];

const EMPTY_INVOICE: Omit<ProjectoInvoiceInsert, 'projecto_id' | 'invoice_type'> = {
  number: null,
  counterparty_name: null,
  counterparty_email: null,
  issue_date: null,
  due_date: null,
  subtotal: 0,
  vat_rate: 0.23,
  total: 0,
  currency: 'EUR',
  status: 'Draft',
  amount_paid: 0,
  paid_date: null,
  notes: null,
  display_order: 0,
};

function fmt(v: number) {
  return v.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' });
}

interface InvoiceFormProps {
  invoice?: ProjectoInvoice | null;
  invoiceType: ProjectoInvoiceType;
  onSave: (data: Omit<ProjectoInvoiceInsert, 'projecto_id'>) => Promise<void>;
  onClose: () => void;
}

function InvoiceForm({ invoice, invoiceType, onSave, onClose }: InvoiceFormProps) {
  const [form, setForm] = useState<Omit<ProjectoInvoiceInsert, 'projecto_id'>>(() =>
    invoice
      ? {
          invoice_type: invoice.invoice_type,
          number: invoice.number,
          counterparty_name: invoice.counterparty_name,
          counterparty_email: invoice.counterparty_email,
          issue_date: invoice.issue_date,
          due_date: invoice.due_date,
          subtotal: invoice.subtotal,
          vat_rate: invoice.vat_rate,
          total: invoice.total,
          currency: invoice.currency,
          status: invoice.status,
          amount_paid: invoice.amount_paid,
          paid_date: invoice.paid_date,
          notes: invoice.notes,
          display_order: invoice.display_order,
        }
      : { ...EMPTY_INVOICE, invoice_type: invoiceType }
  );
  const [saving, setSaving] = useState(false);

  const set = (u: Partial<typeof form>) => {
    setForm((f) => {
      const next = { ...f, ...u };
      if ('subtotal' in u || 'vat_rate' in u) {
        next.total = (next.subtotal ?? 0) * (1 + (next.vat_rate ?? 0));
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent';
  const labelCls = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5';
  const isReceivable = form.invoice_type === 'Receivable';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">
            {invoice ? 'Edit Invoice' : `Add ${invoiceType}`}
          </h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Invoice #</label>
              <input className={inputCls} placeholder="INV-001" value={form.number ?? ''} onChange={(e) => set({ number: e.target.value || null })} />
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <select className={inputCls} value={form.status} onChange={(e) => set({ status: e.target.value as ProjectoInvoiceStatus })}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>{isReceivable ? 'Client Name' : 'Vendor Name'}</label>
              <input className={inputCls} placeholder="Company or person" value={form.counterparty_name ?? ''} onChange={(e) => set({ counterparty_name: e.target.value || null })} />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input type="email" className={inputCls} placeholder="contact@example.com" value={form.counterparty_email ?? ''} onChange={(e) => set({ counterparty_email: e.target.value || null })} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Issue Date</label>
              <input type="date" className={inputCls} value={form.issue_date ?? ''} onChange={(e) => set({ issue_date: e.target.value || null })} />
            </div>
            <div>
              <label className={labelCls}>Due Date</label>
              <input type="date" className={inputCls} value={form.due_date ?? ''} onChange={(e) => set({ due_date: e.target.value || null })} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-xl">
            <div>
              <label className={labelCls}>Subtotal (€)</label>
              <input
                type="number"
                min="0"
                step="100"
                className={inputCls}
                value={form.subtotal ?? 0}
                onChange={(e) => set({ subtotal: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <label className={labelCls}>VAT Rate</label>
              <select
                className={inputCls}
                value={form.vat_rate ?? 0.23}
                onChange={(e) => set({ vat_rate: parseFloat(e.target.value) })}
              >
                <option value={0}>0%</option>
                <option value={0.06}>6%</option>
                <option value={0.13}>13%</option>
                <option value={0.23}>23%</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Total (incl. VAT)</label>
              <div className={`${inputCls} bg-white font-bold text-gray-900`}>
                {fmt(form.total ?? 0)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Amount Paid (€)</label>
              <input
                type="number"
                min="0"
                step="100"
                className={inputCls}
                value={form.amount_paid ?? 0}
                onChange={(e) => set({ amount_paid: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <label className={labelCls}>Paid Date</label>
              <input type="date" className={inputCls} value={form.paid_date ?? ''} onChange={(e) => set({ paid_date: e.target.value || null })} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Notes</label>
            <textarea rows={2} className={`${inputCls} resize-none`} placeholder="Any notes..." value={form.notes ?? ''} onChange={(e) => set({ notes: e.target.value || null })} />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" isLoading={saving} onClick={handleSave}>
            {invoice ? 'Save Changes' : 'Add Invoice'}
          </Button>
        </div>
      </div>
    </div>
  );
}

interface SectionProps {
  type: ProjectoInvoiceType;
  sectionInvoices: ProjectoInvoice[];
  onAdd: (type: ProjectoInvoiceType) => void;
  onEdit: (invoice: ProjectoInvoice) => void;
  onDelete: (invoice: ProjectoInvoice) => void;
}

function InvoiceSection({ type, sectionInvoices, onAdd, onEdit, onDelete }: SectionProps) {
  const isReceivable = type === 'Receivable';
  const Icon = isReceivable ? ArrowUpRight : ArrowDownRight;
  const total = sectionInvoices.reduce((s, i) => s + i.total, 0);
  const paid = sectionInvoices.reduce((s, i) => s + i.amount_paid, 0);
  const outstanding = total - paid;

  const headerColor = isReceivable
    ? 'bg-emerald-50 border-emerald-100 text-emerald-800'
    : 'bg-red-50 border-red-100 text-red-800';

  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden">
      <div className={`flex items-center justify-between px-4 py-3 border-b ${headerColor}`}>
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" />
          <span className="text-sm font-semibold">{isReceivable ? 'Receivables' : 'Payables'}</span>
          <span className="text-xs opacity-60">({sectionInvoices.length})</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-xs text-current opacity-60">Outstanding</div>
            <div className="text-sm font-bold tabular-nums">{fmt(outstanding)}</div>
          </div>
          <button
            onClick={() => onAdd(type)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              isReceivable
                ? 'bg-accent-600 hover:bg-accent-700 text-white'
                : 'bg-red-600 hover:bg-red-700 text-white'
            }`}
          >
            <Plus className="w-3.5 h-3.5" />
            Add {type}
          </button>
        </div>
      </div>

      {sectionInvoices.length === 0 ? (
        <div className="py-8 text-center text-sm text-gray-400">
          <p>No {type.toLowerCase()}s yet.</p>
          <button onClick={() => onAdd(type)} className="mt-1 text-blue-600 hover:underline text-xs font-medium">
            + Add first invoice
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Invoice #</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{isReceivable ? 'Client' : 'Vendor'}</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Issue Date</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Due Date</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Total</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Paid</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Outstanding</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="w-20 py-2 px-3" />
              </tr>
            </thead>
            <tbody>
              {sectionInvoices.map((inv) => {
                const cfg = PROJECTO_INVOICE_STATUS_CONFIG[inv.status];
                const remaining = inv.total - inv.amount_paid;
                const isOverdue = inv.is_overdue;
                return (
                  <tr key={inv.id} className={`border-b border-gray-50 hover:bg-gray-50 group transition-colors ${isOverdue ? 'bg-red-50/40' : ''}`}>
                    <td className="py-2.5 px-3 font-medium text-gray-900">{inv.number || <span className="text-gray-400 italic">Draft</span>}</td>
                    <td className="py-2.5 px-3 text-gray-700">{inv.counterparty_name || '—'}</td>
                    <td className="py-2.5 px-3 text-gray-500 whitespace-nowrap text-xs">
                      {inv.issue_date ? new Date(inv.issue_date).toLocaleDateString('pt-PT') : '—'}
                    </td>
                    <td className={`py-2.5 px-3 text-xs whitespace-nowrap ${isOverdue ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                      {inv.due_date ? new Date(inv.due_date).toLocaleDateString('pt-PT') : '—'}
                      {isOverdue && <span className="ml-1 text-red-500">!</span>}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums font-semibold text-gray-900">{fmt(inv.total)}</td>
                    <td className="py-2.5 px-3 text-right tabular-nums text-emerald-700">{fmt(inv.amount_paid)}</td>
                    <td className={`py-2.5 px-3 text-right tabular-nums font-medium ${remaining > 0 ? 'text-amber-600' : 'text-gray-400'}`}>{fmt(remaining)}</td>
                    <td className="py-2.5 px-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color}`}>
                        {inv.status}
                      </span>
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => onEdit(inv)} className="p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-50 transition-colors">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => onDelete(inv)} className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-200 bg-gray-50">
                <td colSpan={4} className="py-2 px-3 text-xs font-semibold text-gray-500 text-right">Total</td>
                <td className="py-2 px-3 text-right font-bold text-gray-900 tabular-nums">{fmt(total)}</td>
                <td className="py-2 px-3 text-right font-bold text-emerald-700 tabular-nums">{fmt(paid)}</td>
                <td className="py-2 px-3 text-right font-bold text-amber-600 tabular-nums">{fmt(outstanding)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

export function ProjectoInvoicesTab({ projectoId, invoices, onInvoicesChange }: Props) {
  const [formOpen, setFormOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<ProjectoInvoice | null>(null);
  const [formType, setFormType] = useState<ProjectoInvoiceType>('Receivable');

  const receivables = invoices.filter((i) => i.invoice_type === 'Receivable');
  const payables = invoices.filter((i) => i.invoice_type === 'Payable');

  const openAdd = (type: ProjectoInvoiceType) => {
    setEditingInvoice(null);
    setFormType(type);
    setFormOpen(true);
  };

  const openEdit = (invoice: ProjectoInvoice) => {
    setEditingInvoice(invoice);
    setFormType(invoice.invoice_type);
    setFormOpen(true);
  };

  const handleSave = async (data: Omit<ProjectoInvoiceInsert, 'projecto_id'>) => {
    if (editingInvoice) {
      const updated = await projectoInvoiceService.update(editingInvoice.id, projectoId, data);
      onInvoicesChange(invoices.map((i) => (i.id === editingInvoice.id ? updated : i)));
    } else {
      const created = await projectoInvoiceService.create({ ...data, projecto_id: projectoId, display_order: invoices.length });
      onInvoicesChange([...invoices, created]);
    }
  };

  const handleDelete = async (invoice: ProjectoInvoice) => {
    await projectoInvoiceService.delete(invoice.id, projectoId);
    onInvoicesChange(invoices.filter((i) => i.id !== invoice.id));
  };

  return (
    <div className="space-y-6 pb-24">
      <InvoiceSection type="Receivable" sectionInvoices={receivables} onAdd={openAdd} onEdit={openEdit} onDelete={handleDelete} />
      <InvoiceSection type="Payable" sectionInvoices={payables} onAdd={openAdd} onEdit={openEdit} onDelete={handleDelete} />

      {formOpen && (
        <InvoiceForm
          invoice={editingInvoice}
          invoiceType={formType}
          onSave={handleSave}
          onClose={() => { setFormOpen(false); setEditingInvoice(null); }}
        />
      )}
    </div>
  );
}
